import React from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  loginUser,
  socialLogin,
  clearError,
  clearOtpState,
  requestMobileOtp,
  verifyMobileOtp,
} from '../store/slices/authSlice';
import AuthMethodToggle from '../components/auth/AuthMethodToggle';
import AuthShowcase from '../components/auth/AuthShowcase';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { ROUTES } from '../config/appConfig';
import { GOOGLE_CLIENT_ID } from '../config/googleOAuthConfig';
import { normalizeUserRole } from '../utils/apiResponseUtils';
import '../styles/Auth.css';

const PHONE_REGEX = /^[0-9]{10}$/;
const OTP_REGEX = /^[0-9]{6}$/;

const validationSchema = Yup.object().shape({
  email: Yup.string()
    
    .trim().required('Email, phone or username is required'),
  password: Yup.string()
    
    .required('Password is required'),
});

const showcaseFeatures = [
  {
    title: 'Fast booking',
    description: 'Pickup, parcel, payment, and tracking in one guided flow.',
  },
  {
    title: 'Live delivery status',
    description: 'Track pending, assigned, picked up, and delivered updates clearly.',
  },
  {
    title: 'Privacy-first driver details',
    description: 'See assigned driver information without clutter or confusion.',
  },
];

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, otpRequested, otpRequestMeta, user } = useSelector((state) => state.auth);
  const [socialError, setSocialError] = React.useState('');
  const [authMethod, setAuthMethod] = React.useState('email');
  const [mobileForm, setMobileForm] = React.useState({ phone: '', otp: '' });
  const [mobileError, setMobileError] = React.useState('');
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);
  const postLoginFocus = location.state?.postLoginFocus;
  const fromLandingFlow = location.state?.fromLandingFlow === true;

  const navigateToDashboard = React.useCallback(() => {
    const dashboardState = {
      ...(postLoginFocus ? { focusSection: postLoginFocus } : {}),
      ...(fromLandingFlow ? { fromLandingFlow: true } : {}),
    };

    navigate(ROUTES.dashboard, {
      state: Object.keys(dashboardState).length > 0 ? dashboardState : null,
      replace: true,
    });
  }, [fromLandingFlow, navigate, postLoginFocus]);

  React.useEffect(() => {
    if (isAuthenticated) {
      const role = normalizeUserRole(user);

      if (role === 'admin') {
        navigate(ROUTES.adminDashboard, { replace: true });
        return;
      }

      if (role === 'driver') {
        navigate(ROUTES.driverDashboard, { replace: true });
        return;
      }

      navigateToDashboard();
    }
  }, [isAuthenticated, navigate, navigateToDashboard, user]);

  const handleDispatchError = () => {
    setSocialError('');
    setMobileError('');
    dispatch(clearError());
  };

  React.useEffect(() => {
    dispatch(clearOtpState());
  }, [dispatch]);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setSocialError('');

      const result = await dispatch(
        socialLogin({ provider: 'google', token: tokenResponse.access_token, role: 'user' })
      );

      if (result.payload && !result.error) {
        navigateToDashboard();
      }
    },
    onError: () => {
      setSocialError('Google sign-in was cancelled or failed. Please try again.');
    },
  });

  const handleSocialLogin = async (provider) => {
    if (provider === 'google') {
      if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'your_google_client_id_here') {
        setSocialError('Google sign-in is not configured. Set the Google client ID for the current environment in your .env file.');
        return;
      }

      googleLogin();
      return;
    }

    setSocialError('Unsupported social sign-in provider.');
  };

  const resetMobileOtpFlow = () => {
    setMobileForm((current) => ({ ...current, otp: '' }));
    setMobileError('');
    dispatch(clearOtpState());
  };

  const handleAuthMethodChange = (method) => {
    setAuthMethod(method);
    setSocialError('');
    setMobileError('');
    dispatch(clearError());
    dispatch(clearOtpState());
    setMobileForm({ phone: '', otp: '' });
  };

  const validatePhone = () => {
    if (!PHONE_REGEX.test(mobileForm.phone)) {
      setMobileError('Enter a valid 10-digit mobile number.');
      return false;
    }

    return true;
  };

  const handleRequestOtp = async () => {
    if (loading) return;
    dispatch(clearOtpState());
    setMobileForm(current => ({ ...current, otp: '' }));
    if (!validatePhone()) {
      return;
    }

    setMobileError('');
    const result = await dispatch(
      requestMobileOtp({
        phone: mobileForm.phone,
        purpose: 'LOGIN',
        role: 'user',
      })
    );

    if (result.error) {
      return;
    }

    setMobileForm((current) => ({ ...current, otp: '' }));
  };

  const handleVerifyOtp = async () => {
    if (!validatePhone()) {
      return;
    }

    if (loading) return;
    if (!otpRequestMeta?.requestToken) { setMobileError('Request a mobile code first.'); return; }
    if (!OTP_REGEX.test(mobileForm.otp)) {
      setMobileError('Enter the 6-digit OTP sent to your mobile number.');
      return;
    }

    setMobileError('');
    const result = await dispatch(
      verifyMobileOtp({
        phone: mobileForm.phone,
        otp: mobileForm.otp,
        purpose: 'LOGIN',
        role: 'user',
        requestToken: otpRequestMeta?.requestToken,
        otpToken: otpRequestMeta?.otpToken,
      })
    );

    if (result.payload && !result.error) {
      navigateToDashboard();
    }
  };

  return (
    <div className="auth-page">
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xs={12} xl={11}>
            <div className="auth-layout auth-layout-customer">
              <AuthShowcase
                platformLabel="Customer Delivery Platform"
                kicker="Customer Access"
                title="Book, track, and manage deliveries from one calm workspace."
                description="Create a delivery in minutes, stay updated on driver progress, and keep every parcel organized."
                features={showcaseFeatures}
              />

              <div className="auth-card auth-form-card p-4 rounded shadow">
              <div className="auth-heading-block">
                <span className="auth-chip">Welcome Back</span>
                <h2 className="mb-2 fw-bold">Log in to your delivery space</h2>
                <p className="text-muted mb-0">Use email and password, mobile OTP, or Google sign-in to continue.</p>
              </div>

              <AuthMethodToggle
                activeMethod={authMethod}
                ariaLabel="Login method selector"
                onChange={handleAuthMethodChange}
              />


              {(error || socialError || mobileError) && (
                <Alert variant="danger" dismissible onClose={handleDispatchError}>
                  {socialError || mobileError || error}
                </Alert>
              )}

              {authMethod === 'mobile' && otpRequestMeta?.message && otpRequested && (
                <Alert variant="success" dismissible onClose={resetMobileOtpFlow}>
                  {otpRequestMeta.message}
                </Alert>
              )}

              {authMethod === 'email' ? (
              <Formik
                initialValues={{
                  email: '',
                  password: '',
                }}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                  const result = await dispatch(loginUser({ ...values, role: 'user' }));
                  if (result.payload && !result.error) {
                    navigateToDashboard();
                  }
                }}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email, phone, or username</Form.Label>
                      <Form.Control
                        type="text" autoComplete="username"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                        placeholder="you@example.com"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && !!errors.password}
                        placeholder="Enter your password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Button
                      variant="link"
                      type="button"
                      className="auth-inline-link d-block ms-auto px-0 mb-3"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </Button>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 mb-3"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Logging in...
                        </>
                      ) : (
                        'Login'
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>
              ) : (
                <div className="otp-auth-panel">
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="tel"
                      value={mobileForm.phone}
                      onChange={(event) => {
                        setMobileForm((current) => ({
                          ...current,
                          phone: event.target.value.replace(/\D/g, '').slice(0, 10),
                        }));
                        dispatch(clearOtpState());
                        setMobileForm(current => ({ ...current, otp: '' }));
                        setMobileError('');
                      }}
                      placeholder="9876543210"
                      disabled={loading}
                    />
                  </Form.Group>

                  {otpRequested && (
                    <Form.Group className="mb-3">
                      <Form.Label>OTP</Form.Label>
                      <Form.Control
                        type="text"
                        value={mobileForm.otp}
                        onChange={(event) => {
                          setMobileForm((current) => ({
                            ...current,
                            otp: event.target.value.replace(/\D/g, '').slice(0, 6),
                          }));
                          setMobileError('');
                        }}
                        placeholder="Enter 6-digit OTP"
                        disabled={loading}
                      />
                    </Form.Group>
                  )}

                  <div className="otp-action-row mb-3">
                    {!otpRequested ? (
                      <Button variant="primary" className="w-100" onClick={handleRequestOtp} disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Sending OTP...
                          </>
                        ) : (
                          'Send OTP'
                        )}
                      </Button>
                    ) : (
                      <>
                        <Button variant="primary" className="w-100" onClick={handleVerifyOtp} disabled={loading}>
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Verifying...
                            </>
                          ) : (
                            'Login with OTP'
                          )}
                        </Button>
                        <Button variant="outline-secondary" className="w-100" onClick={handleRequestOtp} disabled={loading}>
                          Resend OTP
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="social-divider"><span>or continue with</span></div>
              <div className="d-grid gap-2 mb-3">
                <GoogleSignInButton disabled={loading} onClick={() => handleSocialLogin('google')} />
              </div>

              <p className="text-center">
                Don't have an account? <Link to={ROUTES.signup}>Sign Up</Link>
              </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ForgotPasswordModal
        show={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
        role="user"
      />
    </div>
  );
};

export default Login;
